/**
 * Mehrere Tauchgänge gemeinsam bearbeiten.
 *
 * Ein Dialog über der Ansicht, keine eigene Route: Eine Route würde die
 * Ansicht neu aufbauen und die Markierung verlieren.
 *
 * Die Ansicht rechnet nicht. Was geschrieben wird, entscheidet `sammeln.js`;
 * geschrieben wird über `schreibeZelle`.
 */

import { FELDER, GRUPPEN } from '../felder.js';
import { PLATZFELDER, platzAenderungen, platzWirkung, sammelAenderungen,
  umkehrung } from '../sammeln.js';
import { schreibeSammel } from '../tabelle.js';
import { eingabefeld } from './feldeingabe.js';
import { alsBlatt } from './blatt.js';

/**
 * Felder, die das Sammelformular nicht anbietet.
 *
 * Datum, Uhrzeit, Dauer und Tiefe sind gemessen und gesperrt. Die Nummer ist
 * je Tauchgang eindeutig — derselbe Wert bei zwölf Sätzen ergäbe elf
 * Fehlermeldungen und einen geänderten Tauchgang.
 */
const NICHT_SAMMELBAR = ['start_datum', 'start_zeit', 'dauer_min', 'max_depth_m',
  'dive_number'];

/**
 * Feld plus Leeren-Häkchen.
 *
 * Das Häkchen gibt es nur bei gepflegten Feldern. Was aus dem Import kommt —
 * Temperaturen, Sicht in Metern, Strömung, Blei, Flasche — lässt sich
 * überschreiben, aber nicht entfernen.
 */
function feldZeile(f, vorschlaege) {
  const huelle = eingabefeld(f, '', vorschlaege);
  if (!f.gepflegt) return huelle;
  const leeren = document.createElement('label');
  leeren.className = 'leeren';
  leeren.innerHTML = '<input type="checkbox"> leeren';
  leeren.querySelector('input').name = `leeren-${f.schluessel}`;
  // Wer leeren will, soll nicht gleichzeitig einen Wert eintippen können.
  leeren.querySelector('input').addEventListener('change', (e) => {
    const eingabe = huelle.querySelector(`[name="${f.schluessel}"]`);
    eingabe.disabled = e.target.checked;
    if (e.target.checked) eingabe.value = '';
  });
  huelle.append(leeren);
  return huelle;
}

function buddySchalter() {
  const huelle = document.createElement('div');
  huelle.className = 'buddymodus';
  for (const [wert, text] of [['hinzufuegen', 'hinzufügen'],
    ['entfernen', 'entfernen'], ['ersetzen', 'ersetzen']]) {
    const label = document.createElement('label');
    const knopf = document.createElement('input');
    knopf.type = 'radio';
    knopf.name = 'buddyModus';
    knopf.value = wert;
    knopf.checked = wert === 'hinzufuegen';
    label.append(knopf, document.createTextNode(` ${text}`));
    huelle.append(label);
  }
  return huelle;
}

/**
 * Die Namenskonvention hinter dem Formular auswerten — ohne DOM.
 *
 * Nimmt bereits eingesammelte `{ name, value, checked }` entgegen: Präfix
 * "leeren-" wird abgestreift und landet nur bei angehaktem Kästchen in
 * `leeren`, `buddyModus` fliesst nicht in `werte` und fällt ohne angehakten
 * Schalter auf "hinzufuegen" zurück.
 */
export function eingabenAusListe(eintraege) {
  const werte = {};
  const leeren = [];
  let buddyModus = 'hinzufuegen';
  for (const { name, value, checked } of eintraege) {
    if (name.startsWith('leeren-')) {
      if (checked) leeren.push(name.slice('leeren-'.length));
    } else if (name === 'buddyModus') {
      if (checked) buddyModus = value;
    } else {
      werte[name] = value;
    }
  }
  return { werte, leeren, buddyModus };
}

/** Die Eingaben einsammeln, so wie `sammeln.js` sie erwartet. */
export function eingabenAus(formular) {
  const eintraege = [...formular.querySelectorAll('[name]')]
    .map((el) => ({ name: el.name, value: el.value, checked: el.checked }));
  return eingabenAusListe(eintraege);
}

export function oeffneSammelformular(db, markierte, alleTauchgaenge, plaetze, optionen = {}) {
  const dialog = document.createElement('dialog');
  dialog.className = 'sammelformular';

  const formular = document.createElement('form');
  formular.method = 'dialog';

  const kopf = document.createElement('h2');
  kopf.textContent = `${markierte.length} Tauchgänge bearbeiten`;
  formular.append(kopf);

  const hinweis = document.createElement('p');
  hinweis.className = 'hinweis';
  hinweis.textContent = 'Leer gelassene Felder bleiben unangetastet.';
  formular.append(hinweis);

  // Wie im Tauchgangsformular aus dem Bestand gerechnet, nicht aus den
  // Stammdaten: So erscheint auch ein Name, der noch keinen Stammsatz hat.
  const ausBestand = (feld) => [...new Set(alleTauchgaenge
    .map((t) => t[feld]).filter(Boolean))].sort();
  const vorschlaege = {
    buddies: [...new Set(alleTauchgaenge.flatMap((t) => t.buddies ?? []))].sort(),
    site_name: plaetze.map((p) => p.name).filter(Boolean).sort(),
    trip_name: ausBestand('trip_name'),
    weather: ausBestand('weather'),
  };

  for (const gruppe of GRUPPEN) {
    const felder = FELDER.filter((f) => f.gruppe === gruppe.schluessel
      && !NICHT_SAMMELBAR.includes(f.schluessel));
    if (felder.length === 0) continue;
    const behaelter = gruppe.schluessel === 'kern'
      ? document.createElement('section')
      : document.createElement('details');
    behaelter.className = 'gruppe';
    if (behaelter.tagName === 'DETAILS') {
      const titel = document.createElement('summary');
      titel.textContent = gruppe.titel;
      behaelter.append(titel);
    }
    for (const f of felder) {
      behaelter.append(feldZeile(f, vorschlaege[f.schluessel]));
      if (f.schluessel === 'buddies') behaelter.append(buddySchalter());
    }
    formular.append(behaelter);
  }

  // Platzfelder mit Wirkungsanzeige — sie ändern Stammsätze, nicht Tauchgänge.
  const platz = document.createElement('details');
  platz.className = 'gruppe platzgruppe';
  const platzTitel = document.createElement('summary');
  platzTitel.textContent = 'Tauchplatz';
  platz.append(platzTitel);
  for (const schluessel of PLATZFELDER) {
    const beschriftung = { country: 'Land', region: 'Region', water_name: 'Gewässer' };
    // Platzfelder werden von Hand gepflegt — Land und Region kommen aus dem
    // Reverse-Geocoding, das Gewässer von niemandem. Sie tragen das Häkchen.
    platz.append(feldZeile({ schluessel, beschriftung: beschriftung[schluessel],
      art: 'text', gepflegt: true }));
  }
  const wirkung = platzWirkung(markierte, alleTauchgaenge);
  const wirkungsAbsatz = document.createElement('p');
  wirkungsAbsatz.className = 'wirkung';
  wirkungsAbsatz.textContent = wirkungsText(wirkung);
  platz.append(wirkungsAbsatz);
  formular.append(platz);

  const meldung = document.createElement('p');
  meldung.className = 'sammelmeldung';
  const abbrechen = document.createElement('button');
  abbrechen.type = 'button';
  abbrechen.className = 'knopf';
  abbrechen.textContent = 'Abbrechen';
  const schreiben = document.createElement('button');
  schreiben.type = 'button';
  schreiben.className = 'knopf haupt';
  schreiben.textContent = 'Schreiben';
  const fuss = document.createElement('div');
  fuss.className = 'dialogfuss';
  fuss.append(meldung, abbrechen, schreiben);
  formular.append(fuss);

  dialog.append(formular);
  document.body.append(dialog);
  // Auf dem Telefon faehrt derselbe Dialog als Blatt von unten herein und
  // laesst sich wegschieben; auf breiten Schirmen aendert der Aufruf nichts.
  alsBlatt(dialog);
  dialog.showModal();

  return new Promise((fertig) => {
    // Aufgeräumt und beantwortet wird an genau einer Stelle — `beende()`.
    // Bliebe der Dialog stehen, hätte der nächste seine rund dreissig
    // Feld-Kennungen doppelt, und Beschriftungen wie Vorschlagslisten zeigten
    // auf die Leiche; bliebe das Versprechen offen, wartete der Aufrufer für
    // immer.
    //
    // Angestossen wird `beende()` auf drei Wegen, und keiner davon ist
    // Doppelspurigkeit zum Wegräumen — zusammen sind sie die Zusage, dass der
    // Dialog auf jedem Weg verschwindet:
    //
    // - `close`, der saubere Weg — auf einer Plattform, die das Ereignis
    //   zustellt, deckt er alles ab;
    // - die Knöpfe direkt, weil das Ereignis eben nicht überall ankommt.
    //   Am 23.08.2026 in zwei Chromium-Umgebungen gemessen: `close()` setzt
    //   `open` auf false, der Empfänger läuft nie;
    // - `keydown` auf Escape, weil dasselbe für den Weg gilt, den der Browser
    //   selbst geht — ein Tastenereignis wird dagegen zuverlässig zugestellt.
    //
    // Der Riegel `erledigt` macht das mehrfache Ende harmlos: Der erste Weg
    // gewinnt, die übrigen laufen ins Leere. Wer die Plattform später wieder
    // prüft und `close` zugestellt bekommt, darf aufräumen — vorher nicht.
    let erledigt = false;
    let schreibtGerade = false;
    const beende = (ergebnis) => {
      if (erledigt) return;
      erledigt = true;
      dialog.remove();
      fertig(ergebnis);
    };
    // Escape darf einen laufenden Schreibvorgang nicht beantworten: Die Sätze
    // sind dann längst geschrieben, und der Aufrufer bekäme einen Abbruch
    // gemeldet — keine Rückmeldung, keine aufgefrischte Ansicht. Wer während
    // der Zeile "N von M …" Escape drückt, wartet sie ab.
    const beendeWennFrei = () => { if (!schreibtGerade) beende(null); };
    dialog.addEventListener('close', beendeWennFrei);
    dialog.addEventListener('keydown', (e) => { if (e.key === 'Escape') beendeWennFrei(); });
    abbrechen.addEventListener('click', () => { dialog.close(); beende(null); });
    schreiben.addEventListener('click', async () => {
      const eingaben = eingabenAus(formular);
      const tgAenderungen = sammelAenderungen(markierte, eingaben);
      const platzAenderung = platzAenderungen(markierte, plaetze, eingaben);
      if (tgAenderungen.length + platzAenderung.length === 0) {
        meldung.textContent = 'Nichts ausgefüllt — es gibt nichts zu schreiben.';
        return;
      }
      if (!globalThis.confirm(uebersicht(tgAenderungen, platzAenderung))) return;

      schreiben.disabled = true;
      schreibtGerade = true;
      const melde = (fertigZahl, gesamt) => {
        meldung.textContent = `${fertigZahl} von ${gesamt} …`;
      };
      const tg = await schreibeSammel(db, 'dives', tgAenderungen,
        { geraet: optionen.geraet, melde });
      const orte = await schreibeSammel(db, 'sites', platzAenderung,
        { geraet: optionen.geraet, melde });

      // Zurueckgenommen wird nur, was auch geschrieben wurde: Ein Satz, den
      // schreibeSammel abgelehnt hat, steht noch auf seinem alten Wert.
      const geschriebeneIds = new Set([...tg.staende, ...orte.staende].map((s) => s.id));
      const nurGeschriebene = (liste) => liste.filter(({ satz }) => geschriebeneIds.has(satz.id));
      const erwartet = Object.fromEntries([...tg.staende, ...orte.staende]
        .map((s) => [s.id, s.geaendert]));

      const ruecknahme = geschriebeneIds.size
        ? { erwartet,
          dives: umkehrung(nurGeschriebene(tgAenderungen)),
          sites: umkehrung(nurGeschriebene(platzAenderung)) }
        : null;

      // Erst beenden, dann schliessen: Käme der close-Empfänger zuvor, meldete
      // er `null` — und ein geglücktes Schreiben sähe aus wie ein Abbruch.
      beende({ geschrieben: tg.geschrieben + orte.geschrieben,
        fehler: [...tg.fehler, ...orte.fehler],
        ruecknahme });
      dialog.close();
    });
  });
}

/**
 * Der Text der Rückfrage.
 *
 * Nennt die Mehrflaschen-Tauchgänge, wenn Flaschenfelder betroffen sind: Die
 * Sammelbearbeitung fasst wie das Formular nur die erste Flasche an, und das
 * darf niemand erst hinterher merken.
 */
export function uebersicht(tgAenderungen, platzAenderung) {
  const zeilen = [];
  if (tgAenderungen.length) zeilen.push(`${tgAenderungen.length} Tauchgänge ändern`);
  if (platzAenderung.length) zeilen.push(`${platzAenderung.length} Plätze ändern`);

  const flaschenfelder = tgAenderungen.some(({ felder }) =>
    felder.some((f) => f.schluessel.startsWith('tank_')));
  const mehrflaschig = tgAenderungen
    .filter(({ satz }) => (satz.tanks ?? []).length > 1).length;
  if (flaschenfelder && mehrflaschig) {
    zeilen.push(`${mehrflaschig} davon haben mehrere Flaschen — geändert wird `
      + 'nur die erste.');
  }
  return `${zeilen.join('\n')}\n\nSchreiben?`;
}

/**
 * Der Text zur Wirkung einer Platzänderung.
 *
 * Deutsche Zahlwörter statt blosser Ziffern bei 1 ("ein Platz", "einer",
 * "einem weiteren Tauchgang") — sonst liest sich "davon 1 mit weiteren 1
 * Tauchgängen" wie eine Fehlermeldung. Trifft die Änderung nur einen
 * einzigen Platz, entfällt das "davon" ganz: "wirkt auf 1 Platz, davon
 * einer mit ..." nennt denselben Platz zweimal.
 */
export function wirkungsText(wirkung) {
  const { plaetze, mitFremden, fremdeTauchgaenge } = wirkung;
  if (plaetze === 0) return 'betrifft keinen Tauchplatz';

  const platzWort = plaetze === 1 ? '1 Platz' : `${plaetze} Plätze`;
  if (mitFremden === 0) {
    return `wirkt auf ${platzWort}, weitere Tauchgänge sind nicht betroffen`;
  }

  const tgWort = fremdeTauchgaenge === 1
    ? 'einem weiteren Tauchgang'
    : `weiteren ${fremdeTauchgaenge} Tauchgängen`;
  if (plaetze === 1) return `wirkt auf ${platzWort} mit ${tgWort}`;

  const mitFremdenWort = mitFremden === 1 ? 'einer' : String(mitFremden);
  return `wirkt auf ${platzWort}, davon ${mitFremdenWort} mit ${tgWort}`;
}
